/**
 * @file
 * Functionality for fetching EBAB data.
 */
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { getApiConfig, getApiUri } from '../configUtils';
import { useState, useEffect } from 'react';

import { hash } from 'spark-md5';

import {
    applyDummyPublicToken,
    formatFrontierData,
    getPeerGroupData
} from '../functions/dataFormatting';
import { STATUS } from '../functions/visibility';

/**
 * Custom hook for fetching benchmark data based on the user's authorization status.
 *
 * This hook leverages the Auth0 React SDK to determine the user's authentication status
 * and, if authenticated, retrieves an access token. It fetches data from either the
 * 'scores' endpoint (for authenticated users) or the 'scores/unauthorized' endpoint (for
 * unauthenticated users). The hook manages the state of the fetched data and any
 * encountered errors.
 *
 * The hook uses React's `useState` and `useEffect` to manage the data fetching process
 * and handle state updates based on the authentication status.
 *
 * @returns {object} An object containing:
 *  - `data`: The fetched data array of objects containing benchmark information, or null if an error occurred or data hasn't been fetched yet.
 *  - `error`: The error object if an error occurred during data fetching, or null otherwise.
 */
export const useBenchmarkData = () => {
    const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
    const [benchmarkData, setBenchmarkData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const apiConfig = getApiConfig();
            const endpoint = isAuthenticated ? 'scores' : 'scores/unauthorized';
            const apiUri = getApiUri(apiConfig) + endpoint;
            let requestParams = {};
            if (isAuthenticated) {
                try {
                    const cacheKey = hash(
                        user.subscription_type +
                            process.env.REACT_APP_SUB_TYPE_SALT
                    );
                    const token = await getAccessTokenSilently();
                    requestParams.headers = {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'X-Cache-Key': cacheKey
                    };
                } catch (error) {
                    setError(error);
                    console.error(error);
                    return;
                }
            }

            try {
                const response = await axios.get(apiUri, requestParams);
                if (isAuthenticated) {
                    setBenchmarkData(response.data.items);
                } else {
                    /* dot to table interaction requires a publicToken which unauthorised score items don't have, adding here */
                    setBenchmarkData(
                        applyDummyPublicToken(response.data.items)
                    );
                }
            } catch (error) {
                console.log(error);
                setError(error);
            }
        };

        fetchData();
    }, [isAuthenticated, getAccessTokenSilently]);

    return { benchmarkData, error };
};

/**
 * Custom hook used to format benchmark data and detect the current ebab period.
 * @param {Array<Object>} benchmarkData - Array of benchmark data objects from api
 * @param {Array<Object>} ownershipData - Array of owned portfolios for the user
 * @returns {{frontierData: Array<Object>, ebabPeriod: String}} - Formatted benchmark data array and current ebab period
 */
export function useFrontierData(benchmarkData, ownershipData) {
    const [ebabPeriod, setEbabPeriod] = useState(null);
    const [frontierData, setFrontierData] = useState(null);

    useEffect(() => {
        // the data is rendered only when benchmark data is available
        if (!benchmarkData) {
            return;
        }

        setEbabPeriod(benchmarkData[0]?.ebabPeriod);

        setFrontierData(formatFrontierData(benchmarkData, ownershipData));
    }, [benchmarkData, ownershipData]);

    return {
        frontierData,
        ebabPeriod
    };
}

/**
 * Custom hook to fetch owned portfolios for the currently signed-in user from the /user/owned endpoint.
 * @returns {{ownershipData: Array<Object>, error: Error}} - Object containing the owned portfolios and optionally an error
 */
export function useOwnershipData() {
    const [ownershipData, setOwnershipData] = useState([]);
    const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
    const [error, setError] = useState(null);

    const config = getApiConfig();
    const uri = getApiUri(config) + 'user/owned';

    const setData = async () => {
        const accessToken = await getAccessTokenSilently();

        // allows for caching per user with protections against guessing
        // of the key or downgraded users accessing Pro data
        const cacheKey = hash(
            user.subscription_type +
                user.sub +
                process.env.REACT_APP_SUB_TYPE_SALT
        );

        const requestParams = {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'X-Cache-Key': cacheKey
            },
            params: { userId: user.sub }
        };

        try {
            const response = await axios.get(uri, requestParams);
            setOwnershipData(response.data.items);
        } catch (err) {
            setError(err);
            console.log(err);
            setOwnershipData([]);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            setData();
        }
    }, [user?.sub]);

    return {
        ownershipData,
        error
    };
}

/**
 * Custom hook for fetching data relating to a specific portfolio from the EBAB api.
 * Specify the endpoint required, either /cross-section or /timeseries are supported currently.
 * @param {Object} selectedDot - portfolio object to query
 * @param {String} endpoint - endpoint to fetch from
 * @param {Function} transformer - function to transform response data
 * @returns {Array<Object>} - array of objects relating to that portfolio
 */
export function usePortfolioData(selectedDot, endpoint, transformer = null) {
    const [responseData, setResponseData] = useState([]);
    const { getAccessTokenSilently, user } = useAuth0();
    const [error, setError] = useState(null);
    const config = getApiConfig();

    let uri = getApiUri(config) + 'portfolio/' + endpoint;
    selectedDot?.status === STATUS.owned && (uri += '/owned');

    const cacheKey = hash(
        user.subscription_type + process.env.REACT_APP_SUB_TYPE_SALT
    );

    const setData = async () => {
        const token = await getAccessTokenSilently();
        const requestParams = {
            headers: {
                Authorization: `Bearer ${token}`,
                'X-Cache-Key': cacheKey
            },
            params: { token: selectedDot?.uuid }
        };

        try {
            const response = await axios.get(uri, requestParams);

            if (transformer) {
                setResponseData(transformer(response.data.items));
            } else {
                setResponseData(response.data.items);
            }
        } catch (err) {
            setError(err);
            console.log(err);
            setResponseData([]);
        }
    };

    useEffect(() => {
        setResponseData([]);
        selectedDot?.uuid && setData();
    }, [selectedDot?.uuid]);

    return {
        responseData,
        error
    };
}

/**
 * Custom hook for getting peer group data for a given ebab period from the timeseries data.
 * @param {Array<Object>} timeSeriesData - over time data for given portfolio
 * @param {string} ebabPeriod - the ebabPeriod to fetch
 * @returns {{peerGroupData: Object}} - peer group data object
 */
export function usePeerGroupData(timeSeriesData, ebabPeriod) {
    const [peerGroupData, setPeerGroupData] = useState(null);
    useEffect(() => {
        setPeerGroupData(null);
        if (timeSeriesData) {
            setPeerGroupData(getPeerGroupData(timeSeriesData, ebabPeriod));
        }
    }, [timeSeriesData, ebabPeriod]);

    return {
        peerGroupData
    };
}
